import React from "react";
import {
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { ExportDataTypeInclusionSelector } from "./ExportDataTypeInclusionSelector";
import {
  EXPORT_FIELDS_INCLUSION_TYPES,
  EXPORT_INCLUSION_TYPES,
  type ExportFieldConfiguration,
} from "./types";
import { getCommonExportRatioOptions } from "./utils";
import { FieldConfigurationSelector } from "./FieldConfigurationSelector";
import { LookbackSelector } from "./LookbackSelector";
import { useMentionFilterRenderer } from "./hooks/useMentionFilterRenderer";
import {
  mentionExportFields,
  firmographicsExportFields,
} from "./exportConfigurationFields";
import type { RadioOption } from "../RadioOptionsInput";

const DATA_TYPE = "Mentions";

const MENTION_TIME_SERIES_FIELDS = [
  { value: "hg_id", label: "HG Company ID" },
  { value: "mention_id", label: "Mention ID" },
  { value: "date_verified", label: "Date Verified" },
  { value: "weighted_score", label: "Weighted Score" },
  { value: "document_type_names", label: "Document Type" },
];

interface Props {
  inclusion: EXPORT_INCLUSION_TYPES;
  setInclusion: (value: EXPORT_INCLUSION_TYPES) => void;
  mentionIds?: number[];
  setMentionIds?: (ids: number[]) => void;
  fieldConfiguration: ExportFieldConfiguration;
  setFieldConfiguration: (cfg: ExportFieldConfiguration) => void;
  lookbackMonths: number | string;
  setLookbackMonths: (value: number | string) => void;
  includeTimeSeries: boolean;
  setIncludeTimeSeries: (value: boolean) => void;
  timeSeriesFieldConfiguration?: ExportFieldConfiguration;
  setTimeSeriesFieldConfiguration?: (cfg: ExportFieldConfiguration) => void;
}

export const MentionsExportOptions: React.FC<Props> = ({
  inclusion,
  setInclusion,
  mentionIds = [],
  setMentionIds,
  fieldConfiguration,
  setFieldConfiguration,
  lookbackMonths,
  setLookbackMonths,
  includeTimeSeries,
  setIncludeTimeSeries,
  timeSeriesFieldConfiguration,
  setTimeSeriesFieldConfiguration,
}) => {
  const isDisabled = inclusion === EXPORT_INCLUSION_TYPES.NONE;

  const selectedOption: RadioOption = {
    label: "Include only selected Mentions",
    value: EXPORT_INCLUSION_TYPES.SELECTED,
  };
  const options = [...getCommonExportRatioOptions(DATA_TYPE), selectedOption];

  const renderOptionContent = useMentionFilterRenderer(
    inclusion,
    mentionIds,
    (ids) => setMentionIds?.(ids)
  );

  return (
    <Stack spacing={2} sx={{ width: "70%", paddingTop: "40px" }}>
      <Typography variant="h5" fontWeight={500} fontSize={16}>
        Mentions
      </Typography>
      <ExportDataTypeInclusionSelector
        dataType={DATA_TYPE}
        options={options}
        inclusion={inclusion}
        setInclusion={setInclusion}
        renderOptionContent={renderOptionContent}
      />
      <FieldConfigurationSelector
        fields={mentionExportFields}
        firmographicsFields={firmographicsExportFields}
        value={fieldConfiguration}
        onChange={setFieldConfiguration}
        title="Mention Fields"
        checkboxLabel="Export All Mentions Fields"
        disabled={isDisabled}
      />
      <Stack spacing={1}>
        <Typography variant="subtitle1">
          Mention Last Verified Date Range [Universe Only]
        </Typography>
        <LookbackSelector
          value={lookbackMonths}
          onChange={setLookbackMonths}
          disabled={isDisabled}
        />
      </Stack>
      <Divider />
      <Stack spacing={1}>
        <Typography variant="subtitle1">Time Series [Universe Only]</Typography>
        <Typography variant="body2">
          Please note, Time Series is an independent file that is heavily
          dependent on your Mentions file.
        </Typography>
        <Typography variant="body2">
          To receive a Time Series file, you must also be receiving a Mentions
          file. Any export filters applied to the Mentions file will also apply
          to the Time Series file.
        </Typography>
        <Typography variant="body2">
          Mentions included in Time Series will match the Mention Last Verified
          Date Range you set above. In addition, you will also need to specify
          the date range for the Time Series data itself. This covers which
          mentions qualify to be included, and how much data on those mentions
          we deliver.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={includeTimeSeries}
              onChange={(e) => setIncludeTimeSeries(e.target.checked)}
              disabled={isDisabled}
              data-testid="mentions-time-series-toggle"
            />
          }
          label="Include Time Series data"
        />
        <FieldConfigurationSelector
          fields={MENTION_TIME_SERIES_FIELDS}
          value={
            timeSeriesFieldConfiguration ?? {
              inclusionType: EXPORT_FIELDS_INCLUSION_TYPES.ALL,
              fields: [],
            }
          }
          onChange={setTimeSeriesFieldConfiguration ?? (() => {})}
          title="Mention Time Series Fields"
          checkboxLabel="Export All Mention Time Series Fields"
          disabled={isDisabled || !includeTimeSeries}
        />
      </Stack>
    </Stack>
  );
};
